import { useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  ShiftRequest, 
  ShiftRequestInsert, 
  ShiftRequestUpdate, 
  ShiftRequestWithDetails,
  ShiftRequestStatus 
} from '@/types/shift-components';

interface UseShiftRequestsProps {
  businessId: string | null;
  employeeId?: string | null; // Optional for employee-specific requests
}

interface UseShiftRequestsReturn {
  shiftRequests: ShiftRequestWithDetails[];
  loadingRequests: boolean;
  createShiftRequest: (shiftId: string, employeeNotes?: string) => Promise<void>;
  updateShiftRequestStatus: (requestId: string, status: ShiftRequestStatus, managerNotes?: string) => Promise<void>;
  cancelShiftRequest: (requestId: string) => Promise<void>;
  approveShiftRequest: (requestId: string, managerNotes?: string) => Promise<void>;
  rejectShiftRequest: (requestId: string, managerNotes?: string) => Promise<void>;
  fetchShiftRequests: () => Promise<void>;
  fetchEmployeeShiftRequests: (employeeId: string) => Promise<void>;
  fetchPendingRequests: () => Promise<void>;
}

export const useShiftRequests = ({
  businessId,
  employeeId,
}: UseShiftRequestsProps): UseShiftRequestsReturn => {
  const [shiftRequests, setShiftRequests] = useState<ShiftRequestWithDetails[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);

  // Fetch all shift requests for a business
  const fetchShiftRequests = useCallback(async () => {
    if (!businessId) return;

    setLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from('shift_requests')
        .select(`
          *,
          employees:employee_id(name, email),
          shifts:shift_id(start_time, end_time, title)
        `)
        .eq('business_id', businessId)
        .order('requested_at', { ascending: false });

      if (error) throw error;

      setShiftRequests(data || []);
    } catch (error) {
      console.error('Error fetching shift requests:', error);
      toast.error("Error", { description: "Failed to load shift requests" });
    } finally {
      setLoadingRequests(false);
    }
  }, [businessId]);

  // Fetch shift requests for a specific employee
  const fetchEmployeeShiftRequests = useCallback(async (targetEmployeeId: string) => {
    if (!businessId) return;

    setLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from('shift_requests')
        .select(`
          *,
          employees:employee_id(name, email),
          shifts:shift_id(start_time, end_time, title)
        `)
        .eq('business_id', businessId)
        .eq('employee_id', targetEmployeeId)
        .order('requested_at', { ascending: false });

      if (error) throw error;

      setShiftRequests(data || []);
    } catch (error) {
      console.error('Error fetching employee shift requests:', error);
      toast.error("Error", { description: "Failed to load your shift requests" });
    } finally {
      setLoadingRequests(false);
    }
  }, [businessId]);

  // Fetch only pending requests for business owners
  const fetchPendingRequests = useCallback(async () => {
    if (!businessId) return;

    setLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from('shift_requests')
        .select(`
          *,
          employees:employee_id(name, email),
          shifts:shift_id(start_time, end_time, title)
        `)
        .eq('business_id', businessId)
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });

      if (error) throw error;

      setShiftRequests(data || []);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
      toast.error("Error", { description: "Failed to load pending requests" });
    } finally {
      setLoadingRequests(false);
    }
  }, [businessId]);

  // Create a new shift request (employee action)
  const createShiftRequest = useCallback(async (shiftId: string, employeeNotes?: string) => {
    if (!businessId || !employeeId) {
      toast.error("Error", { description: "Cannot create request: Missing required information" });
      return;
    }

    try {
      // Check if employee already has a pending or approved request for this shift
      const { data: existingRequest, error: checkError } = await supabase
        .from('shift_requests')
        .select('id, status')
        .eq('employee_id', employeeId)
        .eq('shift_id', shiftId)
        .in('status', ['pending', 'approved'])
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingRequest) {
        toast.error("Request Already Exists", { description: `You already have a ${existingRequest.status} request for this shift` });
        return;
      }

      const requestData: ShiftRequestInsert = {
        business_id: businessId,
        employee_id: employeeId,
        shift_id: shiftId,
        employee_notes: employeeNotes || null,
        status: 'pending'
      };

      const { error } = await supabase
        .from('shift_requests')
        .insert([requestData]);

      if (error) throw error;

      toast.success("Request Submitted", { description: "Your shift request has been submitted for approval" });

      // Refresh the requests list
      if (employeeId) {
        await fetchEmployeeShiftRequests(employeeId);
      } else {
        await fetchShiftRequests();
      }
    } catch (error) {
      console.error('Error creating shift request:', error);
      toast.error("Error", { description: "Failed to submit shift request" });
    }
  }, [businessId, employeeId, fetchShiftRequests, fetchEmployeeShiftRequests]);

  // Update shift request status (generic function)
  const updateShiftRequestStatus = useCallback(async (
    requestId: string, 
    status: ShiftRequestStatus, 
    managerNotes?: string
  ) => {
    try {
      const updateData: ShiftRequestUpdate = {
        status,
        manager_notes: managerNotes || null,
        reviewed_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('shift_requests')
        .update(updateData)
        .eq('id', requestId);

      if (error) throw error;

      // If approved, we should assign the employee to the shift
      if (status === 'approved') {
        // Get the shift request details to update the shift
        const { data: requestData, error: fetchError } = await supabase
          .from('shift_requests')
          .select('shift_id, employee_id')
          .eq('id', requestId)
          .single();

        if (fetchError) throw fetchError;

        // Update the shift to assign the employee
        const { error: shiftError } = await supabase
          .from('shifts')
          .update({ employee_id: requestData.employee_id })
          .eq('id', requestData.shift_id);

        if (shiftError) throw shiftError;
      }

      const statusMessage = {
        approved: 'Shift request approved',
        rejected: 'Shift request rejected',
        cancelled: 'Shift request cancelled'
      }[status] || 'Shift request updated';

      toast.success("Success", { description: statusMessage });

      // Refresh the requests list
      await fetchShiftRequests();
    } catch (error) {
      console.error('Error updating shift request:', error);
      toast.error("Error", { description: "Failed to update shift request" });
    }
  }, [fetchShiftRequests]);

  // Approve a shift request (manager action)
  const approveShiftRequest = useCallback(async (requestId: string, managerNotes?: string) => {
    await updateShiftRequestStatus(requestId, 'approved', managerNotes);
  }, [updateShiftRequestStatus]);

  // Reject a shift request (manager action)
  const rejectShiftRequest = useCallback(async (requestId: string, managerNotes?: string) => {
    await updateShiftRequestStatus(requestId, 'rejected', managerNotes);
  }, [updateShiftRequestStatus]);

  // Cancel a shift request (employee action)
  const cancelShiftRequest = useCallback(async (requestId: string) => {
    await updateShiftRequestStatus(requestId, 'cancelled');
  }, [updateShiftRequestStatus]);

  // Auto-fetch requests when businessId or employeeId changes
  useEffect(() => {
    if (businessId) {
      if (employeeId) {
        fetchEmployeeShiftRequests(employeeId);
      } else {
        fetchShiftRequests();
      }
    }
  }, [businessId, employeeId, fetchShiftRequests, fetchEmployeeShiftRequests]);

  return {
    shiftRequests,
    loadingRequests,
    createShiftRequest,
    updateShiftRequestStatus,
    cancelShiftRequest,
    approveShiftRequest,
    rejectShiftRequest,
    fetchShiftRequests,
    fetchEmployeeShiftRequests,
    fetchPendingRequests,
  };
};